import { useEffect, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { logError } from 'setup/logger/logError';
import { isAbortError } from 'shared/utils/errorHandling';
import { redactUrlQueryForLog } from 'shared/utils/signedUrlFileUtils';
import { PDF_PREVIEW_LOAD_TIMEOUT_MS } from '../../app/constants/filePreview';
import { pdfPreviewService } from 'services/fileService/pdfPreviewService';

interface UsePdfThumbnailRenderOptions {
  enabled?: boolean;
  cacheKey?: string;
}

interface UsePdfThumbnailRenderResult {
  imageSrc: string | undefined;
  isLoading: boolean;
  error: boolean;
}

function readCachedThumbnail(
  src: string | undefined,
  thumbnailSize: number,
  cacheKey?: string
): string | undefined {
  if (!src && !cacheKey) {
    return undefined;
  }

  return pdfPreviewService.getThumbnailBlobUrl(thumbnailSize, {
    cacheKey,
    src,
  });
}

function resolveInitialLoading(
  enabled: boolean,
  src: string | undefined,
  thumbnailSize: number,
  cacheKey?: string
): boolean {
  if (!enabled || !src) {
    return true;
  }

  return !readCachedThumbnail(src, thumbnailSize, cacheKey);
}

/**
 * One-shot PDF thumbnail: load page 1 → render JPEG → destroy pdf.js doc.
 * Revisit uses cached JPEG blob URL only.
 */
export function usePdfThumbnailRender(
  src: string | undefined,
  thumbnailSize: number,
  options: UsePdfThumbnailRenderOptions = {}
): UsePdfThumbnailRenderResult {
  const { enabled = true, cacheKey } = options;
  const renderSize = pdfPreviewService.resolveThumbnailRenderSize(thumbnailSize);
  const [imageSrc, setImageSrc] = useState<string | undefined>(() =>
    enabled ? readCachedThumbnail(src, renderSize, cacheKey) : undefined
  );
  const [isLoading, setIsLoading] = useState(() =>
    resolveInitialLoading(enabled, src, renderSize, cacheKey)
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    const cachedBlobUrl = readCachedThumbnail(src, renderSize, cacheKey);

    if (cachedBlobUrl) {
      setImageSrc(cachedBlobUrl);
      setError(false);
      setIsLoading(false);
      return;
    }

    if (!enabled) {
      setImageSrc(undefined);
      setError(false);
      setIsLoading(true);
      return;
    }

    if (!src) {
      setImageSrc(undefined);
      setError(false);
      setIsLoading(true);
      return;
    }

    setImageSrc(undefined);
    setError(false);
    setIsLoading(true);

    let isMounted = true;
    const abortController = new AbortController();
    const renderGeneration = pdfPreviewService.getRenderGeneration();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let renderTask:
      | {
          promise: Promise<void>;
          cancel: () => void;
        }
      | undefined;
    let pdfDocument: PDFDocumentProxy | null = null;

    const loadAndRender = async (): Promise<void> => {
      timeoutId = setTimeout(() => {
        abortController.abort();
      }, PDF_PREVIEW_LOAD_TIMEOUT_MS);

      pdfDocument = await pdfPreviewService.loadThumbnailDocument(
        src,
        abortController.signal
      );

      if (
        !isMounted ||
        renderGeneration !== pdfPreviewService.getRenderGeneration()
      ) {
        return;
      }

      const page = await pdfDocument.getPage(1);

      if (
        !isMounted ||
        renderGeneration !== pdfPreviewService.getRenderGeneration()
      ) {
        page.cleanup();
        return;
      }

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        page.cleanup();
        return;
      }

      const viewport = page.getViewport({ scale: 1 });
      const scale = pdfPreviewService.computeThumbnailScale(
        viewport.width,
        viewport.height,
        renderSize
      );
      const scaledViewport = page.getViewport({ scale });

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      renderTask = page.render({
        canvas,
        canvasContext: context,
        viewport: scaledViewport,
        intent: 'print',
      });

      await renderTask.promise;

      if (
        !isMounted ||
        renderGeneration !== pdfPreviewService.getRenderGeneration()
      ) {
        page.cleanup();
        return;
      }

      const blobUrl = await pdfPreviewService.canvasToBlobUrl(
        canvas,
        pdfPreviewService.thumbnailCanvasJpegQuality
      );
      page.cleanup();

      canvas.width = 0;
      canvas.height = 0;

      if (
        !isMounted ||
        renderGeneration !== pdfPreviewService.getRenderGeneration()
      ) {
        return;
      }

      if (blobUrl) {
        pdfPreviewService.setThumbnailBlobUrl(
          renderSize,
          { cacheKey, src },
          blobUrl
        );
        setImageSrc(blobUrl);
      }

      setIsLoading(false);
    };

    void pdfPreviewService
      .enqueueThumbnailRender(async () => {
        await loadAndRender();
      }, renderGeneration)
      .catch((err) => {
        if (!isMounted || isAbortError(err)) {
          return;
        }

        logError('Error rendering PDF thumbnail:', {
          pdfSrc: redactUrlQueryForLog(src),
          error: err,
        });
        setError(true);
        setIsLoading(false);
      })
      .finally(() => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        if (pdfDocument) {
          pdfDocument.cleanup();
          void pdfDocument.destroy();
          pdfDocument = null;
        }
      });

    return () => {
      isMounted = false;
      abortController.abort();
      renderTask?.cancel();

      if (pdfDocument) {
        pdfDocument.cleanup();
        void pdfDocument.destroy();
        pdfDocument = null;
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [cacheKey, enabled, renderSize, src]);

  return { imageSrc, isLoading, error };
}
